import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import { CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ModalLogout,
    ModalUpdate,
} from "../../Browser/BrowserNavbar/BrowserNavbar";
import { useUser } from "../../common/contexts/userContext";
import ThemeModeToggle from "../../common/ThemeModeToggle";
import { clearSession } from "../../services/authStorage";
import { League } from "../../types/League";
import BetOutcomeIcon from "../components/BetOutcomeIcon";
import "./MobileProfile.scss";
import UploadPhoto from "./UploadPhoto";

export const MobileProfile = () => {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();
  const [showLogout, setShowLogout] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  function handleLogout() {
    setShowLogout(false);
    clearSession();
    navigate("/login");
  }

  if (isLoading) {
    return (
      <div className="mobileProfile__root mobileProfile__root--loading">
        <CircularProgress />
      </div>
    );
  }

  const { perfectBets, correctBets, wrongBets, points } = user.score;
  const totalBets = perfectBets + correctBets + wrongBets;
  const accuracy =
    totalBets > 0
      ? Math.round(((perfectBets + correctBets) / totalBets) * 100)
      : 0;

  return (
    <div className="mobileProfile__root">
      <section className="profile__hero">
        <UploadPhoto buttonLabel="Upload a profile picture" />
        <div className="profile__identity">
          <h2 className="profile__name">{user.username}</h2>
          <button
            className="profile__editName"
            aria-label="Edit username"
            onClick={() => setShowEdit(true)}
          >
            <EditRoundedIcon />
          </button>
        </div>
        {user.email && <span className="profile__email">{user.email}</span>}
      </section>

      <section className="profile__statGrid">
        <div className="profile__stat">
          <span className="profile__statValue profile__statValue--accent">
            {points}
          </span>
          <span className="profile__statLabel">Points</span>
        </div>
        <div className="profile__stat">
          <span className="profile__statValue">{accuracy}%</span>
          <span className="profile__statLabel">Accuracy</span>
        </div>
        <div className="profile__stat">
          <span className="profile__statValue">{totalBets}</span>
          <span className="profile__statLabel">Bets</span>
        </div>
      </section>

      <section className="profile__panel">
        <h3 className="profile__panelTitle">Recent form</h3>
        {user.lastFiveBets && user.lastFiveBets.length > 0 ? (
          <div className="profile__form">
            {user.lastFiveBets.map((outcome, index) => (
              <span className="profile__formIcon" key={`form-${index}`}>
                <BetOutcomeIcon outcome={outcome} />
              </span>
            ))}
          </div>
        ) : (
          <span className="profile__empty">No settled bets yet</span>
        )}
      </section>

      <section className="profile__panel">
        <h3 className="profile__panelTitle">My leagues</h3>
        {(user.leagues ?? []).length > 0 ? (
          <div className="profile__leagues">
            {(user.leagues ?? []).map((league) => (
              <LeagueRow league={league} key={league.id} />
            ))}
          </div>
        ) : (
          <span className="profile__empty">You haven't joined a league yet</span>
        )}
      </section>

      <section className="profile__panel">
        <h3 className="profile__panelTitle">Help</h3>
        <button
          className="profile__settingRow profile__settingRow--button"
          onClick={() => navigate("/rules")}
        >
          <span className="profile__settingLabel">
            <MenuBookRoundedIcon /> How scoring works
          </span>
          <ChevronRightRoundedIcon className="profile__chevron" />
        </button>
        <button
          className="profile__settingRow profile__settingRow--button"
          onClick={() => navigate("/faq")}
        >
          <span className="profile__settingLabel">
            <HelpOutlineRoundedIcon /> FAQ
          </span>
          <ChevronRightRoundedIcon className="profile__chevron" />
        </button>
      </section>

      <section className="profile__panel">
        <h3 className="profile__panelTitle">Settings</h3>
        <div className="profile__settingRow">
          <span className="profile__settingLabel">Appearance</span>
          <ThemeModeToggle label="" />
        </div>
        <button
          className="profile__settingRow profile__settingRow--button"
          onClick={() => setShowEdit(true)}
        >
          <span className="profile__settingLabel">
            <EditRoundedIcon /> Edit username
          </span>
          <ChevronRightRoundedIcon className="profile__chevron" />
        </button>
        <button
          className="profile__settingRow profile__settingRow--button"
          onClick={() => setShowLogout(true)}
        >
          <span className="profile__settingLabel profile__settingLabel--danger">
            <LogoutRoundedIcon /> Log out
          </span>
          <ChevronRightRoundedIcon className="profile__chevron" />
        </button>
      </section>

      <ModalLogout
        open={showLogout}
        onClose={setShowLogout}
        handleLogout={handleLogout}
      />
      <ModalUpdate open={showEdit} onClose={setShowEdit} />
    </div>
  );
};

const LeagueRow: React.FC<{ league: League }> = ({ league }) => {
  const navigate = useNavigate();
  const members = league.usersCount;
  return (
    <button
      className="profile__leagueRow"
      onClick={() =>
        navigate("/ranking", {
          state: { leagueOption: { label: league.name, leagueId: league.id } },
        })
      }
    >
      <span className="profile__leagueIcon">
        {league.leagueImageUrl ? (
          <img src={league.leagueImageUrl} alt={league.name} />
        ) : (
          <GroupsRoundedIcon />
        )}
      </span>
      <span className="profile__leagueInfo">
        <span className="profile__leagueName">{league.name}</span>
        <span className="profile__leagueMeta">
          {members} {members === 1 ? "member" : "members"}
          {league.topPoints ? ` · top ${league.topPoints} pts` : ""}
        </span>
      </span>
      <ChevronRightRoundedIcon className="profile__chevron" />
    </button>
  );
};
